import { useForm, getFormProps } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { json, type ActionFunctionArgs } from '@remix-run/node'
import { Link, redirect, useFetcher, useFetchers, useSubmit } from '@remix-run/react'
import { useRef, useState } from 'react'
import { z } from 'zod'
import { Button } from '#app/components/ui/button.js'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuPortal, DropdownMenuTrigger } from '#app/components/ui/dropdown-menu.js'
import { useRequestInfo } from '#app/utils/request-info.ts'
import { type UOServers, availableUOServers, getServerSlug } from '#app/utils/selected-server-options.js'
import { setSelectedServer } from '#app/utils/selected-server.server.ts'

const SelectedServerFormSchema = z.object({
	selectedServer: z.enum(['', ...availableUOServers]).optional(),
	// this is useful for progressive enhancement
	redirectTo: z.string().optional(),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: SelectedServerFormSchema,
	})

	invariantResponse(submission.status === 'success', 'Invalid server received')

	const { selectedServer, redirectTo } = submission.value
	const responseInit = {
		headers: { 'set-cookie': setSelectedServer(typeof selectedServer === "undefined" ? "" : selectedServer) },
	}
	if (redirectTo) {
		return redirect(redirectTo, responseInit)
	} else {
		return json({ result: submission.reply() }, responseInit)
	}
}

export function ServerSwitch({
	userPreference,
}: {
	userPreference?: UOServers | string | null
}) {
	const formRef = useRef<HTMLFormElement>(null)
	const submit = useSubmit()

	const fetcher = useFetcher<typeof action>()
	const requestInfo = useRequestInfo()
	const [showDropdown, setShowDropdown] = useState(false);

	const [form] = useForm({
		id: 'server-switch',
		lastResult: fetcher.data?.result,
	})

	const optimisticMode = useOptimisticSelectedServerMode()
	const mode = optimisticMode ?? userPreference ?? ''
	// TODO: Make the switch actually change the sub domain instead of just a cookie
	return <></>

return (
	<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="secondary">
					<Link
						to={`/servers`}
						// this is for progressive enhancement
						onClick={(e) => e.preventDefault()}
						className="flex items-center gap-2"
					>

						<span className="text-body-sm font-bold">
							{!mode || mode === "" ? `Choose a Server` : `Server: ${mode}`}
						</span>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">

						<fetcher.Form
							ref={formRef}
							method="POST"
							{...getFormProps(form)}
							action="/resources/server-switch"
						>

		
							{/* <input type="hidden" name="redirectTo" value={requestInfo.path} /> */}
							<input type="hidden" name="redirectTo" value={`/servers`} />
							<input type="hidden" name="selectedServer" value={mode} />
						</fetcher.Form>

					{availableUOServers.map((server) => (	

					<DropdownMenuItem key={`dropdown_${server}`} asChild onSelect={(event) => {
						
						if (formRef?.current) {
							formRef.current.selectedServer.value = server;
							formRef.current.redirectTo.value = `/servers/${getServerSlug(server)}`;
							submit(formRef.current);
						}
					}}>
								<button
									type="submit"
									className="cursor-pointer"
								>
									{server}
								</button>
					</DropdownMenuItem>
					))}

					<DropdownMenuItem asChild onSelect={(event) => {
						
						if (formRef?.current) {
							formRef.current.selectedServer.value = null;
							formRef.current.redirectTo.value = "/servers";
							submit(formRef.current);
						}
					}}>

							<div className="">
								<button
									type="submit"
									className="cursor-pointer"
								>
									Show All
								</button>
							</div>
					</DropdownMenuItem>

					{/* <DropdownMenuItem
						asChild
						// this prevents the menu from closing before the form submission is completed
						onSelect={(event) => {
							event.preventDefault()
							submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef}>
							<Icon className="text-body-md" name="exit">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem> */}
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
)

}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticSelectedServerMode() {
	const fetchers = useFetchers()
	const serverFetcher = fetchers.find(
		(f) => f.formAction === '/resources/server-switch',
	)

	if (serverFetcher && serverFetcher.formData) {
		const submission = parseWithZod(serverFetcher.formData, {
			schema: SelectedServerFormSchema,
		})

		if (submission.status === 'success') {
			return submission.value.selectedServer
		}
	}
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useSelectedSever() {
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticSelectedServerMode()
	if (optimisticMode) {
		return optimisticMode
	}
	return requestInfo.userPrefs.selectedServer ?? ""
}
